import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

export type FontScale = 'default' | 'large' | 'larger';

/** Multiplier applied to the root font-size for each preset. Keep in sync with
 *  the inline bootstrap script in index.html. */
export const FONT_SCALE_VALUES: Record<FontScale, number> = {
  default: 1.15,
  large: 1.3,
  larger: 1.45,
};

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  default: 'Default',
  large: 'Large',
  larger: 'Larger',
};

interface FontScaleContextValue {
  fontScale: FontScale;
  setFontScale: (s: FontScale) => void;
}

const FontScaleContext = createContext<FontScaleContextValue | undefined>(undefined);

const STORAGE_KEY = 'qi.fontScale';

function isFontScale(v: unknown): v is FontScale {
  return v === 'default' || v === 'large' || v === 'larger';
}

function readInitial(defaultScale: FontScale): FontScale {
  if (typeof window === 'undefined') return defaultScale;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isFontScale(stored) ? stored : defaultScale;
}

interface FontScaleProviderProps {
  children: ReactNode;
  defaultScale?: FontScale;
}

/**
 * FontScaleProvider — sets the `--font-scale` CSS variable on `<html>` and
 * persists the choice to localStorage. The root `html` font-size is
 * `calc(100% * var(--font-scale))`, so every rem-based type size scales
 * together. Mirrors ThemeProvider. The signed-in profile value is reconciled
 * into this via FontScaleSync (profile is the source of truth across devices);
 * localStorage is the instant/guest fallback that the index.html bootstrap
 * script applies before first paint to avoid a resize flash.
 */
export function FontScaleProvider({ children, defaultScale = 'default' }: FontScaleProviderProps) {
  const [fontScale, setFontScaleState] = useState<FontScale>(() => readInitial(defaultScale));

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--font-scale',
      String(FONT_SCALE_VALUES[fontScale]),
    );
    try {
      window.localStorage.setItem(STORAGE_KEY, fontScale);
    } catch {
      // ignore quota errors
    }
  }, [fontScale]);

  const value: FontScaleContextValue = { fontScale, setFontScale: setFontScaleState };
  return <FontScaleContext.Provider value={value}>{children}</FontScaleContext.Provider>;
}

export function useFontScale(): FontScaleContextValue {
  const ctx = useContext(FontScaleContext);
  if (!ctx) throw new Error('useFontScale must be used within <FontScaleProvider>');
  return ctx;
}
